import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Ajax from '@/utils/Ajax';
import Currencies from '@/utils/Currencies';
import classNames from 'clsx';
import { ReactComponent as CheckMarkIcon } from '@/img/elal/check-mark.svg';
import {
    COUPON_STATUSES,
    DISCOUNT_TYPES,
    normalizeCouponResponse,
    resolveCouponErrorMessage
} from 'arbitrip-common/client/utils/Coupons';
import Analytics from 'arbitrip-common/client/analytics';
import ProfileStore from '@/stores/ProfileStore';

const CouponCodeComponent = ({ reservation, isFormLocked, onValidated = () => {} }) => {
    const { t } = useTranslation();
    const [coupon, setCoupon] = useState('');
    const [status, setStatus] = useState(COUPON_STATUSES.IDLE);
    const [discountText, setDiscountText] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleApply = async () => {
        if (!coupon.trim()) return;
        setStatus(COUPON_STATUSES.LOADING);

        const payload = {
            reservation_id: reservation?.id,
            code: coupon.trim(),
            main_guest_member_number: reservation?.travelers?.[0]?.frequent_flyer_number,
        };
        let res;
        try {
            res = await Ajax.validateCoupon(payload);
        } catch (e) {
            setDiscountText('');
            setStatus(COUPON_STATUSES.ERROR);
            setErrorMessage(resolveCouponErrorMessage(t, e?.responseJSON));
            try {
                Analytics.actions.interactions.couponRejected(ProfileStore.getProfile(), { requestFailed: true });
            } catch (err) {
                console.error('CouponCodeComponent::: failed to report coupon validation failure', err);
            }
            onValidated({ code: payload.code, eligible: false, discount: null });
            return;
        }

        const base_price = reservation?.deal?.totalPrice || 0;
            const { eligible, discount } = normalizeCouponResponse(res, base_price);
            if (eligible) {
                let displayLabel = '';
                if (discount) {
                    if (discount.type === DISCOUNT_TYPES.PERCENT && Number.isFinite(discount.percent)) {
                        displayLabel = `${discount.percent}%`;
                    } else if (
                        discount.type === DISCOUNT_TYPES.AMOUNT &&
                        Number.isFinite(discount.amount) &&
                        discount.amount > 0
                    ) {
                        const symbol = Currencies.getSymbol(reservation?.deal?.currency);
                        const amountMajor = Math.max(0, discount.amount || 0);
                        displayLabel = `${symbol}${amountMajor.toLocaleString()}`;
                    }
                }
                setDiscountText(displayLabel);
                setErrorMessage('');
                setStatus(COUPON_STATUSES.SUCCESS);
                try {
                    Analytics.actions.interactions.couponAccepted(ProfileStore.getProfile(), payload.code);
                } catch (err) {
                    console.error('CouponCodeComponent::: failed to report accepted coupon', err);
                }
            } else {
                setDiscountText('');
                setErrorMessage(resolveCouponErrorMessage(t, res));
                setStatus(COUPON_STATUSES.ERROR);
                try {
                    Analytics.actions.interactions.couponRejected(ProfileStore.getProfile());
                } catch (err) {
                    console.error('CouponCodeComponent::: failed to report rejected coupon', err);
                }
            }
            onValidated({
                code: payload.code,
                eligible,
                discount,
            });
    };

    const handleRemove = () => {
        setCoupon('');
        setStatus(COUPON_STATUSES.IDLE);
        setDiscountText('');
        setErrorMessage('');
        onValidated(null);
    };

    return (
        <div
            className={`coupon-code ${status !== COUPON_STATUSES.IDLE ? 'coupon-code--active' : ''} ${isFormLocked ? 'disabled' : ''}`}
        >
            {status === COUPON_STATUSES.SUCCESS ? (
                <div className="coupon-code__success" data-testid="coupon-code-success">
                    <CheckMarkIcon aria-hidden="true" focusable="false" />
                    <div className="coupon-code__text">
                        <div className="coupon-code__title">{t('CouponCodeAccepted')}</div>
                        <div className="coupon-code__subtitle">
                            {t('CouponCodeSuccessMessage', { discount: discountText || ' ' })}
                        </div>
                    </div>
                    <button className="coupon-code__remove" onClick={handleRemove}>
                        {t('Remove')}
                    </button>
                </div>
            ) : (
                <>
                    <label htmlFor="coupon-code-input" className="coupon-code__label">
                        {t('HaveACoupon')}
                    </label>

                    <div className="coupon-code__controls">
                        <input
                            id="coupon-code-input"
                            name="coupon-code-input"
                            type="text"
                            data-testid="coupon-code-input"
                            value={coupon}
                            onChange={(e) => setCoupon(e.target.value)}
                            placeholder={t('EnterPromoCode')}
                            className={`coupon-code__input ${
                                status === COUPON_STATUSES.ERROR ? 'coupon-code__input--error' : ''
                            }`}
                            disabled={isFormLocked}
                        />

                        <button
                            onClick={handleApply}
                            data-testid="coupon-code-apply-button"
                            className={classNames('coupon-code__apply', {
                                'coupon-loading': status === COUPON_STATUSES.LOADING,
                                disabled: isFormLocked,
                            })}
                            disabled={status === COUPON_STATUSES.LOADING || isFormLocked}
                        >
                            {status === COUPON_STATUSES.LOADING ? <div className="spinner" /> : t('Apply')}
                        </button>

                        {status !== COUPON_STATUSES.IDLE && (
                            <button className="coupon-code__remove" onClick={handleRemove}>
                                {t('Remove')}
                            </button>
                        )}
                    </div>

                    {status === COUPON_STATUSES.ERROR && (
                        <div className="coupon-code__error">{errorMessage || t('CouponCodeErrorMessage')}</div>
                    )}
                </>
            )}
        </div>
    );
};

export default CouponCodeComponent;
